/**
 * Head metadata + JSON-LD for /services/hiatal-hernia-surgery.
 *
 * Same three blocks as the revision spokes: MedicalWebPage (reviewedBy the
 * physician), MedicalProcedure, and FAQPage mirroring the visible questions.
 * `page` is the full content model and is passed as `null` on the client so
 * the body copy stays out of the shared chunk.
 */
import { schemaDateFields } from "@/lib/schema-dates";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { buildFaqPage, faqPageJson } from "@/lib/faq-schema";
import { HOSPITAL_REF, ORG_REF, PHYSICIAN_REF, SETTING_OUTPATIENT } from "@/lib/medical-schema";
import {
  HIATAL_FIGURES,
  HIATAL_SLEEVE_SCAN,
  HIATAL_SLEEVE_SCAN_SECOND,
  HIATAL_TYPE1_REPAIR,
  HIATAL_TYPE1_ESOPHAGUS,
  HIATAL_TYPE1_COMPLETED,
  HIATAL_TYPE1_WEIGHT,
  HIATAL_TYPE2_SLEEVE_SCAN,
  HIATAL_TYPE2_PATIENT_STORY,
  HIATAL_TYPE3_SLEEVE_SCAN,
  HIATAL_TYPE3_SLEEVE_SCAN_SECOND,
  HIATAL_TYPE3_PATIENT_STORY,
  HIATAL_TYPE4_PATIENT_STORY,
  HIATAL_TYPE4_UPPER_GI,
  HIATAL_RECURRENT_SCAR_TISSUE,
  HIATAL_RECURRENT_COMPLETED_REPAIR,
  HIATAL_RECURRENT_REDO_2,
  HIATAL_RECURRENT_REDO_2_COMPLETE,
  HIATAL_META,
  HIATAL_PAGE,
  hiatalImageObjects,
} from "@/lib/hiatal-hernia";
import { procedureImageObjects } from "@/lib/procedure-media";

const SITE = "https://virginiabariatric.com";
const CARD = `${SITE}/og/services/og-hiatal-hernia-repair.jpg`;
const CARD_ALT =
  "Hiatal hernia surgery and repair with Dr. Matthew Fitzer in Northern Virginia - medical illustration of normal esophageal hiatus anatomy on a Virginia Bariatric branded card";

type HiatalPage = typeof HIATAL_PAGE;

export function hiatalHerniaHead(page: HiatalPage | null) {
  const meta = HIATAL_META;
  const url = `${SITE}${meta.path}`;
  const photoObjects = [
    ...hiatalImageObjects(HIATAL_FIGURES, "en-US", url, [HIATAL_SLEEVE_SCAN_SECOND, HIATAL_TYPE2_SLEEVE_SCAN, ...HIATAL_TYPE2_PATIENT_STORY, HIATAL_TYPE3_SLEEVE_SCAN, HIATAL_TYPE3_SLEEVE_SCAN_SECOND, ...HIATAL_TYPE3_PATIENT_STORY, HIATAL_SLEEVE_SCAN, HIATAL_TYPE1_REPAIR, HIATAL_TYPE1_ESOPHAGUS, HIATAL_TYPE1_COMPLETED, HIATAL_TYPE1_WEIGHT, HIATAL_TYPE4_UPPER_GI, ...HIATAL_TYPE4_PATIENT_STORY, HIATAL_RECURRENT_SCAR_TISSUE, HIATAL_RECURRENT_COMPLETED_REPAIR, HIATAL_RECURRENT_REDO_2, HIATAL_RECURRENT_REDO_2_COMPLETE]),
    ...procedureImageObjects(["sleeveUgiRefluxPreEn", "sleeveUgiRefluxPostEn"], "en", url),
  ];
  const normalFigureUrl = `${SITE}${HIATAL_FIGURES.normal.full}`;
  const primaryImage = hiatalImageObjects(HIATAL_FIGURES, "en-US", url).find(
    (image) => image.contentUrl === normalFigureUrl,
  );

  return {
    meta: [
      { title: meta.title },
      { name: "description", content: meta.description },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: meta.title },
        { property: "og:description", content: meta.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "en_US" },
        { property: "og:image", content: CARD },
         { property: "og:image:width", content: "1200" },
         { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: CARD_ALT },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: meta.title },
        { name: "twitter:description", content: meta.description },
        { name: "twitter:image", content: CARD },
        { name: "twitter:image:alt", content: CARD_ALT },
      ] : []),
    ],
    links: [...(import.meta.env.SSR ? canonicalAndAlternates(meta.path) : [])],
    scripts: ((import.meta.env.SSR && page) ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          "@id": `${url}#webpage`,
          ...schemaDateFields(meta.path, page.lastReviewed),
          reviewedBy: PHYSICIAN_REF,
          name: page.webPageName,
          url,
          description: meta.description,
          mainEntity: { "@id": `${url}#procedure` },
          inLanguage: "en-US",
          isPartOf: {
            "@type": "WebSite",
            name: "Virginia Bariatric Surgery",
            url: SITE,
          },
          significantLink: [
            `${SITE}/services`,
            `${SITE}/services/sleeve`,
            `${SITE}/services/revisions/sleeve`,
            `${SITE}/services/bypass`,
          ],
          ...(primaryImage ? { primaryImageOfPage: primaryImage } : {}),
          mentions: [HOSPITAL_REF],
          about: [{ "@id": `${url}#procedure` }, {
            "@type": "MedicalCondition",
            name: "Hiatal hernia",
            code: {
              "@type": "MedicalCode",
              codingSystem: "ICD-10",
              codeValue: "K44.9",
            },
          }],
          specialty: { "@type": "MedicalSpecialty", name: "Bariatric Surgery" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Patient",
            geographicArea: { "@type": "AdministrativeArea", name: "Virginia" },
          },
          mainContentOfPage: { "@type": "WebPageElement", cssSelector: "article" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          "@id": `${url}#procedure`,
          mainEntityOfPage: { "@id": `${url}#webpage` },
          procedureType: "https://schema.org/SurgicalProcedure",
          name: page.procedureName,
          alternateName: page.procedureAlternateNames,
          description: page.procedureDescription,
          url,
          image: photoObjects,
          bodyLocation: "Esophageal hiatus and stomach",
          howPerformed:
            "Performed laparoscopically through small incisions: the hernia is reduced, the hernia sac is removed, the esophagus is mobilized, and the crura are closed with sutures, with a fundoplication added when indicated. Often performed at the same time as bariatric surgery. " +
            SETTING_OUTPATIENT +
            " When combined with sleeve or bypass surgery, the repair is part of that operation's one-night stay at INOVA Fair Oaks Hospital.",
          preparation:
            "Upper GI contrast study and, when needed, upper endoscopy and pH testing, along with the standard pre-operative medical evaluation.",
          followup:
            "Staged post-operative diet from liquids to solid food over several weeks, no heavy lifting for four weeks, and long-term follow-up.",
          status: "https://schema.org/ActiveActionStatus",
          medicineSystem: "https://schema.org/WesternConventional",
          relevantSpecialty: { "@type": "MedicalSpecialty", name: "Bariatric Surgery" },
          recognizingAuthority: ORG_REF,
        }),
      },
      {
        type: "application/ld+json",
        children: faqPageJson(
          buildFaqPage(page.faqs, {
            id: `${url}#faq`,
            url,
            name: `${page.h1}: frequently asked questions`,
          }) ?? {},
        ),
      },
    ] : []).filter((script) => script.children !== ""),
  };
}
